import { useCallback, useEffect, useState } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Room } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { StateCard } from '@/components/clinical';
import { useSession } from '@/features/sessions/hooks/useSession';
import type { SidePanelId } from '@/features/video/components/CallControlBar';
import { PreJoinScreen } from '@/features/video/components/PreJoinScreen';
import { VideoCallStage } from '@/features/video/components/VideoCallStage';
import { useCallStatus } from '@/features/video/hooks/useCallStatus';
import { useClientCallRealtime } from '@/features/video/hooks/useClientCallRealtime';
import { useJoinCall } from '@/features/video/hooks/useJoinCall';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/_client-fullscreen/client/sessions/$sessionId/call')({
  component: ClientVideoCallRoute,
});

/**
 * Client video-call route — deliberately narrow compared to the admin
 * version at `_staff-fullscreen/.../call.tsx`.
 *
 * Differences that matter:
 * - Clients never call `POST /sessions/:id/call/end` (integration guide §10.2).
 *   The "End call" button performs a local LiveKit disconnect only; the
 *   room stays open for the practitioner until they end it.
 * - Recording controls (`onStartRecording`, `onStopRecording`) and
 *   participant-management controls (`onToggleParticipants`) are NOT passed
 *   to `VideoCallRoom`, which role-gates them off when the props are absent.
 * - `effectiveClass` is hardcoded to `'client'` rather than derived from
 *   the join response; the backend's role claim is a defence-in-depth
 *   check but the FE never exposes practitioner affordances to this route.
 */
function ClientVideoCallRoute() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('clientSessions');

  const [phase, setPhase] = useState<'pre-join' | 'live'>('pre-join');
  const [devices, setDevices] = useState<{
    cameraId?: string;
    micId?: string;
    audioOnly?: boolean;
    /** Initial publish state from the lobby. Default true on both. */
    cameraEnabled?: boolean;
    micEnabled?: boolean;
  }>({});
  const [localRoom, setLocalRoom] = useState<Room | null>(null);
  const [openPanel, setOpenPanel] = useState<SidePanelId | null>(null);

  function handleTogglePanel(id: SidePanelId) {
    setOpenPanel((current) => (current === id ? null : id));
  }

  const sessionQuery = useSession(sessionId);
  const callStatus = useCallStatus(sessionId);
  const join = useJoinCall();
  const { mutate: joinCall } = join;

  const handleDidNotConnect = useCallback(() => {
    toast.error(t('call.notConnected'));
    if (localRoom) {
      // Already waiting inside the (empty) LiveKit room: disconnect locally and
      // let LiveKitRoom's onDisconnected (returnToDetail) perform the single
      // navigation, mirroring handleLeave — navigating here too would push a
      // duplicate history entry.
      void localRoom.disconnect();
    } else {
      void navigate({
        to: '/client/sessions/$sessionId',
        params: { sessionId } as never,
      });
    }
  }, [t, navigate, sessionId, localRoom]);
  useClientCallRealtime(sessionId, { onDidNotConnect: handleDidNotConnect });

  // Fire the token fetch only when the user clicks Join on the pre-join screen.
  useEffect(() => {
    if (phase === 'live') joinCall(sessionId);
  }, [phase, joinCall, sessionId]);

  // Surface the backend's actual reason via toast and return to the session
  // detail page. Business errors (e.g. "scheduled time has passed") must not
  // be masked by a generic "Couldn't join" full-screen error.
  useEffect(() => {
    if (!join.isError) return;
    const raw = join.error.message;
    toast.error(raw.length > 0 ? raw : t('call.joinErrorDescription'));
    void navigate({
      to: '/client/sessions/$sessionId',
      params: { sessionId } as never,
    });
  }, [join.isError, join.error, navigate, sessionId, t]);

  const handleMediaDeviceFailure = useCallback((_failure: unknown, kind?: MediaDeviceKind) => {
    if (kind === 'videoinput') {
      toast.mediaDeviceError('camera');
    } else if (kind === 'audioinput') {
      toast.mediaDeviceError('mic');
    }
  }, []);

  function returnToDetail() {
    void navigate({
      to: '/client/sessions/$sessionId',
      params: { sessionId } as never,
    });
  }

  function handleLeave() {
    // Local disconnect only — clients never terminate the room for others.
    void localRoom?.disconnect();
  }

  // Wait for session data before rendering PreJoinScreen so the `mode`
  // prop doesn't flip from default 'online' to the real value mid-render.
  if (phase === 'pre-join' && !sessionQuery.data) {
    return (
      <StateCard
        variant="empty"
        headline={t('call.loadingHeadline')}
        description={t('call.loadingDescription')}
      />
    );
  }

  if (phase === 'pre-join') {
    // Clients can only book online sessions in the first place; this guard
    // is belt-and-braces for legacy helpline-mode sessions.
    const mode: 'online' | 'helpline' =
      sessionQuery.data?.sessionMode === 'helpline' ? 'helpline' : 'online';
    return (
      <PreJoinScreen
        sessionId={sessionId}
        mode={mode}
        callStatus={callStatus.data}
        isStatusError={callStatus.isError}
        onRefreshStatus={() => {
          void callStatus.refetch();
        }}
        onJoin={(chosen) => {
          setDevices(chosen);
          setPhase('live');
        }}
        onCancel={returnToDetail}
      />
    );
  }

  // On error the effect above toasts the backend message and navigates back;
  // render a transient empty state while that navigation lands.
  if (join.isError || !join.data) {
    return (
      <StateCard
        variant="empty"
        headline={t('call.joiningHeadline')}
        description={t('call.joiningDescription')}
      />
    );
  }

  const mode: 'online' | 'helpline' =
    sessionQuery.data?.sessionMode === 'helpline' ? 'helpline' : 'online';

  // Honour the lobby toggles. cameraEnabled/micEnabled === false means the
  // client wants to join muted/blanked — passing `false` to LiveKitRoom skips
  // publishing that track entirely. They can flip it back on from the live
  // bottom bar once inside the room. Mirrors the practitioner path in
  // VideoCallScreen.
  const videoConstraint: boolean | { deviceId: string } =
    devices.audioOnly || mode === 'helpline' || devices.cameraEnabled === false
      ? false
      : devices.cameraId != null
        ? { deviceId: devices.cameraId }
        : true;

  const audioConstraint: boolean | { deviceId: string } =
    devices.micEnabled === false
      ? false
      : devices.micId != null
        ? { deviceId: devices.micId }
        : true;

  const envUrl = import.meta.env.VITE_LIVEKIT_URL as string | undefined;
  const serverUrl = envUrl != null && envUrl.length > 0 ? envUrl : join.data.livekitUrl;

  return (
    <LiveKitRoom
      token={join.data.token}
      serverUrl={serverUrl}
      connect
      video={videoConstraint}
      audio={audioConstraint}
      onDisconnected={returnToDetail}
      onMediaDeviceFailure={handleMediaDeviceFailure}
      data-lk-theme="default"
    >
      <VideoCallStage
        effectiveClass="client"
        mode={mode}
        audioOnly={devices.audioOnly}
        isPaused={false}
        openPanel={openPanel}
        isRecordingPending={false}
        sessionNames={{
          clientName: sessionQuery.data?.clientName,
          practitionerName: sessionQuery.data?.practitionerName,
          practitionerKind: sessionQuery.data?.practitionerKind,
        }}
        sessionId={sessionId}
        actualStart={sessionQuery.data?.actualStart ?? null}
        clientUserId={sessionQuery.data?.clientId ?? null}
        practitionerUserId={sessionQuery.data?.practitionerId ?? null}
        onLeave={handleLeave}
        onRoomReady={setLocalRoom}
        onTogglePanel={handleTogglePanel}
      />
    </LiveKitRoom>
  );
}
